import React from 'react';
import { ClipboardList, CheckCircle2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader, ProgressBar } from '../components/Card';

const journeySteps = [
  'Start Saving', 'Pre-Approval', 'House Hunting', 'Offer Accepted',
  'Home Inspection', 'Financing', 'Closing Day', 'Move-In'
];

export default function Reports() {
  const { timeline, setTimeline, getInspectionStats, getTaskStats, budget, getMonthlyPayment, getTotalMonthlyCost, getDownPaymentAmount, getTotalDownPaymentSources } = useApp();
  const inspStats = getInspectionStats();
  const taskStats = getTaskStats();

  return (
    <div>
      <PageHeader
        icon={<ClipboardList size={22} />}
        title="Reports & Timeline"
        subtitle="View your complete home buying journey"
      />

      {/* Set Timeline */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Set Your Timeline</h3>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 6 }}>Target Closing Date</label>
          <input type="date"
            value={timeline.targetClosingDate}
            onChange={e => setTimeline(prev => ({ ...prev, targetClosingDate: e.target.value }))}
            style={{
              padding: '10px 14px', border: '1px solid var(--gray-300)',
              borderRadius: 'var(--radius-sm)', fontSize: 14, width: 260,
            }}
          />
        </div>
      </Card>

      {/* Home Buying Journey */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Your Home Buying Journey</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-600)' }}>Offer Acceptance Date</label>
            <input type="date" value={timeline.offerAcceptanceDate}
              onChange={e => setTimeline(prev => ({ ...prev, offerAcceptanceDate: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 13, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-600)' }}>Condition Removal Date</label>
            <input type="date" value={timeline.conditionRemovalDate}
              onChange={e => setTimeline(prev => ({ ...prev, conditionRemovalDate: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 13, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-600)' }}>Target Closing Date</label>
            <input type="date" value={timeline.targetClosingDate}
              onChange={e => setTimeline(prev => ({ ...prev, targetClosingDate: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 13, marginTop: 4 }} />
          </div>
        </div>

        {/* Journey Steps */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', padding: '0 8px' }}>
          <div style={{
            position: 'absolute', top: '50%', left: 24, right: 24,
            height: 3, background: 'var(--gray-200)', transform: 'translateY(-50%)', zIndex: 0,
          }}>
            <div style={{
              height: '100%',
              width: `${((timeline.currentStage - 1) / (journeySteps.length - 1)) * 100}%`,
              background: 'var(--green)', borderRadius: 3,
            }} />
          </div>
          {journeySteps.map((step, i) => {
            const stepNum = i + 1;
            const isCompleted = stepNum < timeline.currentStage;
            const isCurrent = stepNum === timeline.currentStage;
            return (
              <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1, flex: 1 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 600,
                  background: isCompleted ? 'var(--green)' : isCurrent ? 'white' : 'var(--gray-100)',
                  color: isCompleted ? 'white' : isCurrent ? 'var(--green)' : 'var(--gray-500)',
                  border: isCurrent ? '2px solid var(--green)' : isCompleted ? 'none' : '1px solid var(--gray-300)',
                }}>
                  {isCompleted ? <CheckCircle2 size={18} /> : stepNum}
                </div>
                <span style={{
                  fontSize: 10, marginTop: 6, textAlign: 'center',
                  color: isCompleted || isCurrent ? 'var(--dark)' : 'var(--gray-500)',
                  fontWeight: isCurrent ? 600 : 400,
                }}>{step}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Summary Report */}
      <Card style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Summary Report</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ padding: 16, background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Financial Overview</div>
            {[
              ['Purchase Price', `$${budget.purchasePrice.toLocaleString()}`],
              ['Down Payment', `$${getDownPaymentAmount().toLocaleString()} (${budget.downPaymentPercent}%)`],
              ['Savings Available', `$${getTotalDownPaymentSources().toLocaleString()}`],
              ['Monthly Payment', `$${Math.round(getMonthlyPayment()).toLocaleString()}`],
              ['Total Monthly Cost', `$${getTotalMonthlyCost().toLocaleString()}`],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, borderBottom: '1px solid var(--gray-200)' }}>
                <span style={{ color: 'var(--gray-600)' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: 16, background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Progress Overview</div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                <span>Inspection</span>
                <span>{inspStats.reviewed}/{inspStats.total}</span>
              </div>
              <ProgressBar value={inspStats.reviewed} max={inspStats.total} color="var(--green)" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                <span>Move-In Tasks</span>
                <span>{taskStats.completed}/{taskStats.total}</span>
              </div>
              <ProgressBar value={taskStats.completed} max={taskStats.total} color="var(--green)" />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                <span>Journey Stage</span>
                <span>{timeline.currentStage}/{journeySteps.length}</span>
              </div>
              <ProgressBar value={timeline.currentStage} max={journeySteps.length} color="var(--green)" />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
